/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useState } from 'react';

import { styleguideColors } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily } from '@kaiizen/kai-shiki/core';
import { CameraIcon } from '@kaiizen/kai-shiki/react';

import { pxToRem } from '@/Client/Styles/pxToRem';
import type {
  FocusedGalleryGroup,
  FocusedGallerySection,
} from './gallerySections';
import { useGalleryPhotoUpload } from './useGalleryPhotoUpload';

const EMPTY_STATE_STYLE = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100%;
  padding: 96px 24px 72px;
  text-align: center;
`;

const ICON_STYLE = css`
  display: flex;
  margin-bottom: 26px;
  color: ${styleguideColors.textSecondary};
`;

const TITLE_STYLE = css`
  margin: 0 0 14px;
  color: ${styleguideColors.ink};
  font-family: ${styleguideFontFamily.display};
  font-size: ${pxToRem(26)};
  font-weight: 700;
  line-height: 1.15;
`;

const DESCRIPTION_STYLE = css`
  max-width: 40ch;
  margin: 0;
  color: ${styleguideColors.textSecondary};
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(16)};
  line-height: 1.6;
`;

const WALKTHROUGH_BUTTON_STYLE = css`
  margin-top: 32px;
  padding: 14px 20px;
  border: 1px solid ${styleguideColors.ink};
  background: ${styleguideColors.ink};
  color: ${styleguideColors.white};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(12)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #000;
  }

  &:disabled {
    opacity: 0.42;
    cursor: not-allowed;
  }
`;

const FINE_PRINT_STYLE = css`
  margin-top: 14px;
  color: ${styleguideColors.textTertiary};
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(12)};
  line-height: 1.5;
`;

const ROOM_UPLOADS_STYLE = css`
  width: 100%;
  max-width: 600px;
  margin-top: 38px;
`;

const ROOM_TOGGLE_STYLE = css`
  display: inline-block;
  padding: 4px 11px;
  border: 0;
  background: ${styleguideColors.accent};
  color: ${styleguideColors.ink};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(11)};
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  transform: skewX(-12deg);
  cursor: pointer;

  &:hover {
    filter: brightness(0.96);
  }
`;

const ROOM_TOGGLE_LABEL_STYLE = css`
  display: inline-block;
  transform: skewX(12deg);
`;

const ROOM_LIST_STYLE = css`
  margin-top: 10px;
  text-align: left;
`;

const ROOM_ROW_STYLE = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 2px;
`;

const ROOM_NAME_STYLE = css`
  color: ${styleguideColors.ink};
  font-family: ${styleguideFontFamily.body};
  font-size: ${pxToRem(13)};
  white-space: nowrap;
`;

const ROOM_RULE_STYLE = css`
  flex: 1;
  min-width: 12px;
  height: 1px;
  background: ${styleguideColors.stroke};
`;

const NO_PHOTOS_STYLE = css`
  flex-shrink: 0;
  padding: 3px 7px;
  border: 1px solid ${styleguideColors.error};
  color: ${styleguideColors.error};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ADD_BUTTON_STYLE = css`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid ${styleguideColors.stroke};
  background: transparent;
  color: ${styleguideColors.textSecondary};
  font-family: ${styleguideFontFamily.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    border-color: ${styleguideColors.ink};
    background: ${styleguideColors.white};
    color: ${styleguideColors.ink};
  }
`;

const GalleryEmptyUploadRow = ({
  section,
  scanIsEditable,
}: {
  section: FocusedGallerySection;
  scanIsEditable: boolean;
}) => {
  const { inputRef, openFilePicker, onSelectFiles } = useGalleryPhotoUpload(section);

  return (
    <div css={ROOM_ROW_STYLE}>
      <span css={ROOM_NAME_STYLE}>{section.title}</span>
      <span css={ROOM_RULE_STYLE} aria-hidden='true' />
      <span css={NO_PHOTOS_STYLE}>No photos</span>
      {scanIsEditable
        ? (
          <>
            <button
              type='button'
              css={ADD_BUTTON_STYLE}
              aria-label={`Add photos to ${section.title}`}
              onClick={openFilePicker}
            >
              + Add
            </button>
            <input
              ref={inputRef}
              type='file'
              accept='image/*'
              multiple
              hidden
              onChange={onSelectFiles}
            />
          </>
        )
        : null}
    </div>
  );
};

/** Photo-empty Gallery onboarding with walkthrough and manual room uploads. */
export const GalleryEmptyState = ({
  overview,
  groups,
  scanIsEditable,
  walkthroughUploadEnabled,
  onUploadWalkthrough,
}: {
  overview: FocusedGallerySection;
  groups: readonly FocusedGalleryGroup[];
  scanIsEditable: boolean;
  walkthroughUploadEnabled: boolean;
  onUploadWalkthrough?: () => void;
}) => {
  const [roomsOpen, setRoomsOpen] = useState(false);

  return (
    <div css={EMPTY_STATE_STYLE} data-gallery-empty-state='true'>
      <span css={ICON_STYLE} aria-hidden='true'>
        <CameraIcon size={46} color='currentColor' />
      </span>
      <h2 css={TITLE_STYLE}>Walk it once. Kai does the rest.</h2>
      <p css={DESCRIPTION_STYLE}>
        One walkthrough video becomes every photo you need — sorted by room, attached to the right task, with a draft scope already written.
      </p>
      <button
        type='button'
        css={WALKTHROUGH_BUTTON_STYLE}
        disabled={!walkthroughUploadEnabled || !onUploadWalkthrough}
        onClick={onUploadWalkthrough}
      >
        Upload a walkthrough
      </button>
      <div css={FINE_PRINT_STYLE}>Up to 10 mins. You can upload more photos afterwards.</div>
      <div css={ROOM_UPLOADS_STYLE}>
        <button
          type='button'
          css={ROOM_TOGGLE_STYLE}
          aria-expanded={roomsOpen}
          aria-controls='gallery-empty-room-uploads'
          onClick={() => setRoomsOpen(open => !open)}
        >
          <span css={ROOM_TOGGLE_LABEL_STYLE}>Or upload by room</span>
        </button>
        {roomsOpen
          ? (
            <div id='gallery-empty-room-uploads' css={ROOM_LIST_STYLE}>
              <GalleryEmptyUploadRow section={overview} scanIsEditable={scanIsEditable} />
              {groups.map(group => <GalleryEmptyUploadRow key={group.id} section={group} scanIsEditable={scanIsEditable} />)}
            </div>
          )
          : null}
      </div>
    </div>
  );
};
